// Informes por puesto, procedimientos y atención prioritaria (lote D, M49) → Checklist del Anexo 1.
// Ítems: GT 5 (GT-05; y GT-15, GT-16 y GT-17 por agente), GT 6 (GT-06), GT 7 (GT-07), GT 37 (GT-09) y TH 1 (TH-01).
// Reglas DECIDIDAS POR CLAUDE, por validar con Kevin (12 meses y 30 días: sugeridos por StorSafe, no normativos):
//  - GT 5 (GT-05, 01/11/2026): cada puesto con trabajadores activos y exposición a agentes cuenta como medido si tiene
//    mediciones hechas en StorSafe (ruido, iluminación o estrés térmico; COSHH y BIOGAVAL son evaluaciones
//    cualitativas, no mediciones) O un informe por puesto aprobado (su última versión) con al menos un adjunto de
//    laboratorio. Cumple: todos medidos; el texto dice qué fuente se usó en cada puesto. En proceso: a los que faltan
//    les queda un informe en preparación. No cumple: a alguno no le queda nada. Pendiente: no hay puestos.
//  - GT 6 y 7 (y GT-15/16/17, por agente): cada puesto con trabajadores activos (en GT-15/16/17, con exposición al
//    agente de ese ítem) tiene un informe aprobado y vigente (12 meses, o hasta que cambie una evaluación del puesto).
//    Cumple: todos vigentes. En proceso: alguno en borrador o vencido hace 30 días o menos. No cumple: alguno sin
//    informe o vencido hace más de 30 días. Pendiente: no hay puestos.
//  - GT 37: cada tipo de permiso usado en los últimos 12 meses tiene su procedimiento aprobado y al menos un registro
//    de socialización firmado de su versión vigente. Pendiente: ningún permiso en 12 meses. En proceso: algún
//    procedimiento en borrador o sin socializar. No cumple: algún tipo usado sin procedimiento.
//    Si la empresa todavía no tiene procedimientos, GT 37 sigue con la regla anterior de Permisos de Trabajo.
//  - GT 9 (GT-14): cada medida implementada (cerrada) de la última versión aprobada de cada informe de medidas tiene
//    una verificación in situ eficaz en los últimos 12 meses → Cumple. En proceso: alguna sin verificar dentro de 30
//    días desde su implementación, o "no eficaz" con una medida nueva en el puesto todavía sin verificar. No cumple:
//    alguna vencida, sin verificar después de 30 días o "no eficaz" sin medida nueva. Sin informes aprobados, GT 9
//    sigue con Inspecciones v2 o el módulo anterior.
//  - TH 1: Cumple: el 100 % de los trabajadores activos tiene su condición declarada (también "sin condición") y cada
//    puesto con al menos un trabajador prioritario tiene su informe de GT 6 aprobado, vigente y con medidas para ese
//    grupo. En proceso: falta declarar a algunos o falta ese informe. Pendiente: nadie declarado.
//  - Lo marcado a mano nunca se sobrescribe ("el cálculo automático dice: X" y "Usar cálculo automático"); nunca se
//    toca un "No aplica"; solo se escriben los ítems que aplican según el motor del Checklist. Solo corre si la
//    empresa ya usa el módulo (algún informe, procedimiento o condición declarada).

import { supabase } from '../../supabaseClient';
import { diaEc, fechaEc } from '../fechas';
import type { Traza } from '../pe/pe-tipos';
import { esManual, type EstadoGuardadoChecklist, type EstadoItem, type TrazasPlanV2 } from '../plan-emergencias-compliance-sync';
import { plural } from '../plurales';
import { ETIQUETA_TIPO_PERMISO, TIPOS_PERMISO, type TipoPermiso } from './ip-procedimientos';
import { diasEntre, estadoInformePuesto, isoDia, sumarMesesIso, ultimaVersion, type ResultadoPuesto } from './ip-logica';
import { AGENTE_DE_ITEM, DIAS_TOLERANCIA, ETIQUETA_MODULO, LEGAL_INFORMES, MESES_VIGENCIA, NOTA_PLAZOS, NOTA_VERIFICACION, type Agente,
  type EstadoMedida, type Evaluacion, type FilaVerificacion, type FilaVersion, type Modulo, type TipoInforme } from './ip-tipos';

export const FUENTE_INFORMES_PUESTO = 'informes_puesto';
export const ITEMS_INFORMES = ['GT-05', 'GT-06', 'GT-07', 'GT-15', 'GT-16', 'GT-17'] as const;
export const ITEM_PROCEDIMIENTOS = 'GT-09';
export const ITEM_PRIORITARIA = 'TH-01';
export const ITEM_VERIFICACION = 'GT-14';
export const ITEMS_LOTE_D: string[] = [...ITEMS_INFORMES, ITEM_PROCEDIMIENTOS, ITEM_VERIFICACION, ITEM_PRIORITARIA];
export const MODULO_LOTE_D: Record<string, string> = {
  'GT-05': 'Informes por puesto', 'GT-06': 'Informes por puesto', 'GT-07': 'Informes por puesto', 'GT-15': 'Informes por puesto',
  'GT-16': 'Informes por puesto', 'GT-17': 'Informes por puesto', 'GT-09': 'Procedimientos de trabajos especiales',
  'GT-14': 'Informes por puesto (verificación in situ de las medidas)',
  'TH-01': 'Trabajadores (atención prioritaria)',
};
export const NORMA_REGLA_LOTE_D = 'Criterio técnico de StorSafe, sugerido y no normativo';
const FUENTE_ANEXO1 = 'Anexo 1, Lista de verificación de cumplimiento de obligaciones de seguridad y salud en el trabajo (Ministerio del Trabajo)';
const TIPO_DE_ITEM: Record<string, TipoInforme> = { 'GT-05': 'agentes', 'GT-06': 'riesgos', 'GT-07': 'medidas', 'GT-15': 'agentes', 'GT-16': 'agentes', 'GT-17': 'agentes' };

export interface ResultadoItemLoteD { estado: EstadoItem; resumen: string; trazas: Traza[] }
const estadoTraza = (e: EstadoItem): Traza['estado'] => (e === 'cumple' ? 'cumple' : e === 'no_cumple' ? 'no_cumple' : 'por_validar');
const traza = (item: string, articulo: string, umbral: number, valor: number, unidad: string, estado: EstadoItem, nota: string): Traza => ({
  metodo: 'informes_puesto', norma: NORMA_REGLA_LOTE_D, articulo_o_tabla: `${LEGAL_INFORMES[item] ?? item}: ${articulo}`, umbral, valor, unidad,
  estado: estadoTraza(estado), fuente_doc: FUENTE_ANEXO1, nota,
});

// ── GT 5, 6 y 7 ──
export interface PuestoEstado {
  id: string; nombre: string; activos: number; agentes: Agente[]; resultado: Record<TipoInforme, ResultadoPuesto>;
  /** GT 5: módulos de StorSafe con mediciones del puesto (ruido, iluminación, estrés térmico). */
  mediciones: Modulo[];
  /** GT 5: adjuntos de laboratorio de la última versión aprobada del informe de agentes (0 sin versión aprobada). */
  laboratorio: number;
  /** GT 5: el puesto tiene un informe de agentes (en borrador o aprobado). */
  informeAgentes: boolean;
}

/** Un puesto del cálculo: sus evaluaciones, su borrador y su última versión aprobada de cada tipo (con sus adjuntos de laboratorio). */
export interface EntradaPuesto { id: string; nombre: string; activos: number; evaluaciones: Evaluacion[];
  informes: Partial<Record<TipoInforme, { borrador: boolean; version: Pick<FilaVersion, 'approved_at' | 'eval_fingerprint'> | null; huella: string; evals: Evaluacion[];
    laboratorio?: number }>> }

/** GT 5: módulos de StorSafe que son mediciones. COSHH (químico por tarea) y BIOGAVAL son evaluaciones cualitativas. */
export const MODULOS_MEDICION: Modulo[] = ['ruido', 'iluminacion', 'estres_termico'];

export function estadoPuestos(puestos: EntradaPuesto[], hoy: Date): PuestoEstado[] {
  return puestos.map(p => {
    const res = {} as Record<TipoInforme, ResultadoPuesto>;
    for (const t of ['agentes', 'riesgos', 'medidas'] as TipoInforme[]) {
      const i = p.informes[t];
      res[t] = estadoInformePuesto(!!i?.borrador, i?.version ?? null, i?.huella ?? '', i?.evals ?? [], hoy);
    }
    const agentes = Array.from(new Set(p.evaluaciones.filter(e => ['fisico', 'quimico', 'biologico'].includes(e.agente)).map(e => e.agente)));
    const mediciones = MODULOS_MEDICION.filter(m => p.evaluaciones.some(e => e.modulo === m));
    const ia = p.informes.agentes;
    return { id: p.id, nombre: p.nombre, activos: p.activos, agentes, resultado: res, mediciones,
      laboratorio: ia?.version ? (ia.laboratorio ?? 0) : 0, informeAgentes: !!ia && (ia.borrador || !!ia.version) };
  });
}

/** Puestos que cuentan para el ítem: con trabajadores activos y, en GT 5, con exposición a agentes (del agente del ítem). */
export function puestosDelItem(item: string, puestos: PuestoEstado[]): PuestoEstado[] {
  const conTrab = puestos.filter(p => p.activos > 0);
  // GT 5: la exposición sale de las evaluaciones del puesto o de un informe aprobado con resultados de laboratorio.
  if (item === 'GT-05') return conTrab.filter(p => p.agentes.length > 0 || p.laboratorio > 0);
  if (AGENTE_DE_ITEM[item]) return conTrab.filter(p => p.agentes.includes(AGENTE_DE_ITEM[item]));
  return conTrab;
}

// ── GT 5: mediciones hechas en StorSafe o informe aprobado con adjunto de laboratorio ──
export type FuenteGT5 = 'storsafe' | 'laboratorio' | 'ambas';
/** Qué fuente cuenta en el puesto (null = ninguna). */
export function fuenteGT5(p: PuestoEstado): FuenteGT5 | null {
  const s = p.mediciones.length > 0, l = p.laboratorio > 0;
  return s && l ? 'ambas' : s ? 'storsafe' : l ? 'laboratorio' : null;
}
const listaNombres = (n: string[]) => (n.length <= 1 ? n.join('') : `${n.slice(0, -1).join(', ')} y ${n[n.length - 1]}`);

export function resultadoGT5(puestos: PuestoEstado[]): ResultadoItemLoteD {
  const lista = puestosDelItem('GT-05', puestos);
  const con = lista.map(p => ({ p, f: fuenteGT5(p) }));
  const medidos = con.filter(x => x.f);
  const faltan = con.filter(x => !x.f).map(x => x.p);
  const sinNada = faltan.filter(p => !p.informeAgentes);
  const estado: EstadoItem = lista.length === 0 ? 'pendiente' : faltan.length === 0 ? 'cumple' : sinNada.length ? 'no_cumple' : 'en_proceso';
  const que = 'con trabajadores activos y exposición a agentes';
  const total = plural(lista.length, 'puesto', 'puestos');
  const por = (f: FuenteGT5) => medidos.filter(x => x.f === f).map(x => x.p);
  const modulos = (ps: PuestoEstado[]) => Array.from(new Set(ps.flatMap(p => p.mediciones))).map(m => ETIQUETA_MODULO[m].toLowerCase());
  const fuentes = [
    por('storsafe').length ? `Con mediciones hechas en StorSafe (${listaNombres(modulos(por('storsafe')))}): ${listaNombres(por('storsafe').map(p => p.nombre))}.` : '',
    por('laboratorio').length ? `Con un informe por puesto aprobado con adjunto de laboratorio: ${listaNombres(por('laboratorio').map(p => p.nombre))}.` : '',
    por('ambas').length ? `Con mediciones hechas en StorSafe y un informe aprobado con adjunto de laboratorio: ${listaNombres(por('ambas').map(p => p.nombre))}.` : '',
  ].filter(Boolean).join(' ');
  const resumen = estado === 'pendiente' ? `No hay puestos ${que}.`
    : estado === 'cumple' ? `${total} ${que}, con sus mediciones. ${fuentes}`
      : `De ${total} ${que}, ${faltan.length === 1 ? 'uno no tiene' : `${faltan.length} no tienen`} mediciones hechas en StorSafe ni un informe aprobado `
        + `con adjunto de laboratorio: ${listaNombres(faltan.map(p => p.nombre))}.`
        + (estado === 'en_proceso' ? ' Su informe de agentes está en preparación: apruébalo con el informe de laboratorio adjunto.' : '')
        + (fuentes ? ` ${fuentes}` : '');
  return { estado, resumen, trazas: [
    traza('GT-05', 'mediciones hechas en StorSafe o informe por puesto aprobado con adjunto de laboratorio', lista.length, medidos.length,
      'puestos con mediciones', estado, resumen),
  ] };
}

export function resultadoItemInformes(item: string, puestos: PuestoEstado[]): ResultadoItemLoteD {
  if (item === 'GT-05') return resultadoGT5(puestos);
  const tipo = TIPO_DE_ITEM[item];
  const lista = puestosDelItem(item, puestos);
  const r = lista.map(p => p.resultado[tipo]);
  const vigentes = r.filter(x => x.estado === 'vigente').length;
  const sinInforme = r.filter(x => x.estado === 'sin_informe').length;
  const vencidos = r.filter(x => x.estado === 'vencido').length;
  const enProceso = r.filter(x => x.estado === 'borrador' || x.estado === 'vencido_reciente').length;
  const estado: EstadoItem = lista.length === 0 ? 'pendiente' : sinInforme + vencidos > 0 ? 'no_cumple' : enProceso > 0 ? 'en_proceso' : 'cumple';
  const que = item === 'GT-05' ? 'con trabajadores activos y exposición a agentes' : AGENTE_DE_ITEM[item]
    ? `con trabajadores activos y ${AGENTE_DE_ITEM[item] === 'fisico' ? 'agentes físicos' : AGENTE_DE_ITEM[item] === 'quimico' ? 'agentes químicos' : 'agentes biológicos'}`
    : 'con trabajadores activos';
  const total = plural(lista.length, 'puesto', 'puestos');
  const resumen = estado === 'pendiente' ? `No hay puestos ${que}.`
    : estado === 'cumple' ? `${total} ${que}, con su informe aprobado y vigente.`
      : estado === 'no_cumple' ? `De ${total} ${que}: ${[sinInforme ? plural(sinInforme, 'sin informe', 'sin informe') : '',
        vencidos ? `${plural(vencidos, 'informe vencido', 'informes vencidos')} hace más de ${DIAS_TOLERANCIA} días` : ''].filter(Boolean).join(' y ')}.`
        : `De ${total} ${que}: ${plural(enProceso, 'informe en borrador o vencido hace poco', 'informes en borrador o vencidos hace poco')}.`;
  return { estado, resumen, trazas: [
    traza(item, 'informe por puesto aprobado y vigente', lista.length, vigentes, `puestos con informe vigente (${MESES_VIGENCIA} meses)`, estado, `${resumen} ${NOTA_PLAZOS}`),
  ] };
}

// ── GT 37 ──
export interface PermisoMin { permit_type: string; valid_from: string | null; created_at: string; status: string | null }
export interface ProcedimientoMin { id: string; permit_type: string }
export interface SocializacionMin { procedure_version_id: string | null; status: string }

export function resultadoProcedimientos(permisos: PermisoMin[], procs: ProcedimientoMin[], versiones: Pick<FilaVersion, 'id' | 'procedure_id' | 'version'>[],
  socializaciones: SocializacionMin[], hoy: Date): ResultadoItemLoteD & { usados: TipoPermiso[] } {
  const hoyIso = isoDia(hoy);
  const desde = sumarMesesIso(hoyIso, -MESES_VIGENCIA);
  const usados = TIPOS_PERMISO.filter(t => permisos.some(p => p.permit_type === t && p.status !== 'cancelado'
    && diaEc(p.valid_from ?? p.created_at) >= desde && diaEc(p.valid_from ?? p.created_at) <= hoyIso));
  const firmadas = new Set(socializaciones.filter(s => s.status === 'firmado' && s.procedure_version_id).map(s => s.procedure_version_id as string));
  let listos = 0; const sinProc: TipoPermiso[] = []; const pend: TipoPermiso[] = [];
  for (const t of usados) {
    const p = procs.find(x => x.permit_type === t);
    if (!p) { sinProc.push(t); continue; }
    const v = ultimaVersion(versiones.filter(x => x.procedure_id === p.id));
    if (v && firmadas.has(v.id)) listos++; else pend.push(t);
  }
  const estado: EstadoItem = usados.length === 0 ? 'pendiente' : sinProc.length ? 'no_cumple' : pend.length ? 'en_proceso' : 'cumple';
  const nombres = (ts: TipoPermiso[]) => ts.map(t => ETIQUETA_TIPO_PERMISO[t].toLowerCase()).join(', ');
  const resumen = estado === 'pendiente' ? `No hay permisos de trabajo en los últimos ${MESES_VIGENCIA} meses.`
    : estado === 'cumple' ? `${plural(usados.length, 'tipo de trabajo especial usado', 'tipos de trabajo especial usados')} en ${MESES_VIGENCIA} meses, con su procedimiento aprobado y socializado con firmas.`
      : estado === 'no_cumple' ? `Sin procedimiento: ${nombres(sinProc)}.`
        : `Procedimiento sin aprobar o sin socialización firmada: ${nombres(pend)}.`;
  return { estado, resumen, usados, trazas: [
    traza('GT-09', 'procedimiento aprobado y socializado por cada tipo de permiso usado', usados.length, listos, `tipos listos (${MESES_VIGENCIA} meses)`, estado, `${resumen} ${NOTA_PLAZOS}`),
  ] };
}

// ── GT 9 (GT-14): verificación in situ de las medidas implementadas ──
/** Una medida del puesto para GT 9, con su estado ya resuelto desde su origen (Matriz, Incidentes o propia). */
export interface MedidaGT9 { id: string; puesto_id: string | null; estado: EstadoMedida; cerrada_el: string | null; creada: string; descripcion: string }
export type VerificacionMin = Pick<FilaVerificacion, 'measure_id' | 'verified_on' | 'result' | 'created_at'>;
export type EstadoVerificacion = 'vigente' | 'por_verificar' | 'sin_verificar' | 'vencida' | 'no_eficaz' | 'no_eficaz_con_medida_nueva' | 'reemplazada';
export interface VerificacionMedida { estado: EstadoVerificacion; item: EstadoItem; ultima: VerificacionMin | null; texto: string }

const fechaDma = (iso: string) => fechaEc(iso);
const sumarDiasIso = (iso: string, dias: number) => {
  const [a, m, d] = diaEc(iso).split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, d + dias)).toISOString().slice(0, 10);
};
const ordenVer = (a: VerificacionMin, b: VerificacionMin) => a.verified_on.localeCompare(b.verified_on) || a.created_at.localeCompare(b.created_at);
export const ultimaVerificacion = (id: string, vers: VerificacionMin[]): VerificacionMin | null =>
  vers.filter(v => v.measure_id === id).sort(ordenVer).pop() ?? null;
const eficazVigente = (v: VerificacionMin | null, hoyIso: string) => !!v && v.result === 'eficaz' && sumarMesesIso(v.verified_on, MESES_VIGENCIA) > hoyIso;

/** Estado de la verificación in situ de una medida implementada (cerrada). Plazos sugeridos por StorSafe, no normativos. */
export function verificacionMedida(m: MedidaGT9, todas: MedidaGT9[], vers: VerificacionMin[], hoy: Date): VerificacionMedida {
  const hoyIso = isoDia(hoy);
  const v = ultimaVerificacion(m.id, vers);
  if (v?.result === 'eficaz') {
    const hasta = sumarMesesIso(v.verified_on, MESES_VIGENCIA);
    return hasta > hoyIso
      ? { estado: 'vigente', item: 'cumple', ultima: v, texto: `Verificada eficaz el ${fechaDma(v.verified_on)}; vigente hasta el ${fechaDma(hasta)}.` }
      : { estado: 'vencida', item: 'no_cumple', ultima: v, texto: `La última verificación (eficaz, ${fechaDma(v.verified_on)}) venció el ${fechaDma(hasta)}.` };
  }
  if (v?.result === 'no_eficaz') {
    const nuevas = todas.filter(x => x.id !== m.id && !!x.puesto_id && x.puesto_id === m.puesto_id && diaEc(x.creada) >= v.verified_on);
    if (!nuevas.length) return { estado: 'no_eficaz', item: 'no_cumple', ultima: v,
      texto: `Verificada no eficaz el ${fechaDma(v.verified_on)}, sin una medida nueva en el puesto.` };
    return nuevas.some(x => x.estado === 'cerrada' && eficazVigente(ultimaVerificacion(x.id, vers), hoyIso))
      ? { estado: 'reemplazada', item: 'cumple', ultima: v, texto: `Verificada no eficaz el ${fechaDma(v.verified_on)}; la medida nueva del puesto ya está verificada eficaz.` }
      : { estado: 'no_eficaz_con_medida_nueva', item: 'en_proceso', ultima: v,
        texto: `Verificada no eficaz el ${fechaDma(v.verified_on)}; hay una medida nueva en el puesto: falta implementarla y verificarla.` };
  }
  if (!m.cerrada_el) return { estado: 'por_verificar', item: 'en_proceso', ultima: null, texto: 'Implementada; falta su verificación in situ.' };
  const limite = sumarDiasIso(m.cerrada_el, DIAS_TOLERANCIA);
  return diasEntre(m.cerrada_el, hoyIso) <= DIAS_TOLERANCIA
    ? { estado: 'por_verificar', item: 'en_proceso', ultima: null, texto: `Implementada el ${fechaDma(m.cerrada_el)}; verificación in situ sugerida hasta el ${fechaDma(limite)}.` }
    : { estado: 'sin_verificar', item: 'no_cumple', ultima: null,
      texto: `Implementada el ${fechaDma(m.cerrada_el)} y sin verificación in situ (plazo sugerido: ${fechaDma(limite)}).` };
}

/** enInformes: ids de las medidas de la última versión aprobada de cada informe de medidas (GT 7). */
export function resultadoVerificacionGT9(enInformes: Set<string>, medidas: MedidaGT9[], vers: VerificacionMin[], hoy: Date): ResultadoItemLoteD {
  const impl = medidas.filter(m => enInformes.has(m.id) && m.estado === 'cerrada');
  const r = impl.map(m => verificacionMedida(m, medidas, vers, hoy));
  const ok = r.filter(x => x.item === 'cumple').length;
  const sinVigente = r.filter(x => x.estado === 'vencida' || x.estado === 'sin_verificar').length;
  const noEficaz = r.filter(x => x.estado === 'no_eficaz').length;
  const pend = r.filter(x => x.item === 'en_proceso').length;
  const estado: EstadoItem = impl.length === 0 ? 'en_proceso' : sinVigente + noEficaz > 0 ? 'no_cumple' : pend > 0 ? 'en_proceso' : 'cumple';
  const total = plural(impl.length, 'medida implementada', 'medidas implementadas');
  const resumen = impl.length === 0 ? 'Hay informes de medidas aprobados, pero ninguna de sus medidas está implementada (cerrada con evidencia) todavía.'
    : estado === 'cumple' ? `${total} de los informes aprobados, con su verificación in situ eficaz en los últimos ${MESES_VIGENCIA} meses.`
      : estado === 'no_cumple' ? `De ${total} de los informes aprobados: ${[sinVigente ? `${sinVigente} sin verificación in situ vigente` : '',
        noEficaz ? `${noEficaz} verificada${noEficaz === 1 ? '' : 's'} no eficaz${noEficaz === 1 ? '' : 'es'} sin una medida nueva` : ''].filter(Boolean).join(' y ')}.`
        : `De ${total} de los informes aprobados: ${plural(pend, 'medida por verificar dentro del plazo sugerido', 'medidas por verificar dentro del plazo sugerido')}.`;
  return { estado, resumen, trazas: [
    traza(ITEM_VERIFICACION, 'verificación in situ de las medidas implementadas de los informes aprobados', impl.length, ok,
      `medidas con verificación eficaz (${MESES_VIGENCIA} meses)`, estado, `${resumen} ${NOTA_VERIFICACION}`),
  ] };
}

// ── TH 1 ──
export interface ResumenPrioritaria { activos: number; declarados: number; con_condicion: number;
  por_puesto: { job_position_id: string; activos: number; declarados: number; con_condicion: number }[] }

/** puestosGT6: puestos con informe de GT 6 aprobado, vigente y con medidas para el grupo prioritario. */
export function resultadoPrioritaria(r: ResumenPrioritaria, puestosGT6ConMedidas: Set<string>): ResultadoItemLoteD {
  const faltan = Math.max(0, r.activos - r.declarados);
  const conPrioritarios = r.por_puesto.filter(p => p.con_condicion > 0);
  const sinInforme = conPrioritarios.filter(p => !puestosGT6ConMedidas.has(p.job_position_id)).length;
  const estado: EstadoItem = r.declarados === 0 ? 'pendiente' : faltan > 0 || sinInforme > 0 ? 'en_proceso' : 'cumple';
  const resumen = estado === 'pendiente' ? 'Ningún trabajador activo tiene su condición de atención prioritaria declarada.'
    : estado === 'cumple' ? `Los ${r.activos} trabajadores activos tienen su condición declarada y cada puesto con trabajadores de atención prioritaria tiene su informe de riesgos aprobado con medidas para ese grupo.`
      : [faltan ? `Falta declarar la condición de ${plural(faltan, 'trabajador activo', 'trabajadores activos')} (de ${r.activos}).` : '',
        sinInforme ? `${plural(sinInforme, 'puesto con trabajadores de atención prioritaria no tiene', 'puestos con trabajadores de atención prioritaria no tienen')} su informe de riesgos aprobado con medidas para ese grupo.` : '']
        .filter(Boolean).join(' ');
  return { estado, resumen, trazas: [
    traza('TH-01', 'condición declarada de cada trabajador activo (también "sin condición")', r.activos, r.declarados, 'trabajadores declarados', faltan ? 'en_proceso' : estado, resumen),
    traza('TH-01', 'informe de riesgos del puesto con medidas para el grupo de atención prioritaria', conPrioritarios.length,
      conPrioritarios.length - sinInforme, 'puestos con informe y medidas', sinInforme ? 'en_proceso' : 'cumple',
      'El informe solo indica cuántos trabajadores del puesto tienen una condición considerada, sin nombres ni tipo de condición.'),
  ] };
}

// ── Escribir el Checklist ──
/** ¿Qué se escribe? null = nada. Lo manual (auto_source NULL) nunca se reemplaza; "No aplica" tampoco. */
export function decisionLoteD(existente: EstadoGuardadoChecklist | undefined, deseado: EstadoItem, aplica: boolean): EstadoItem | null {
  if (!aplica) return null;
  if (existente?.estado === 'no_aplica') return null;
  if (esManual(existente)) return null;
  if (existente?.estado === deseado && existente.auto_source === FUENTE_INFORMES_PUESTO) return null;
  if (!existente && deseado === 'pendiente') return null;
  return deseado;
}

/** Escribe lo que decide el cálculo y devuelve la presentación (como las firmas y las inspecciones). */
export async function escribirLoteD(companyId: string, res: Record<string, ResultadoItemLoteD>, aplicables: Set<string>): Promise<TrazasPlanV2> {
  const items = Object.keys(res).filter(i => aplicables.has(i));
  if (!items.length) return {};
  const { data: existentes, error } = await supabase.from('checklist_item_estados').select('item_id, estado, auto_source')
    .eq('company_id', companyId).in('item_id', items);
  if (error) return {};
  const porItem = new Map(((existentes ?? []) as EstadoGuardadoChecklist[]).map(e => [e.item_id, e]));
  const ahora = new Date().toISOString();
  const filas = items.flatMap(item => {
    const d = decisionLoteD(porItem.get(item), res[item].estado, true);
    return d ? [{ company_id: companyId, item_id: item, estado: d, auto_source: FUENTE_INFORMES_PUESTO, updated_at: ahora }] : [];
  });
  if (filas.length) {
    const { error: eu } = await supabase.from('checklist_item_estados').upsert(filas, { onConflict: 'company_id,item_id' });
    if (eu) console.error('syncInformesPuesto', eu);
  }
  return Object.fromEntries(items.map(item => {
    const previo = porItem.get(item);
    const manual = esManual(previo);
    return [item, { ...res[item], manualRespetado: manual, estadoManual: manual ? previo!.estado : null, modulo: MODULO_LOTE_D[item], fuente: FUENTE_INFORMES_PUESTO }];
  }));
}

// "Usar cálculo automático": quita el estado manual y escribe el cálculo. Nunca sobre "No aplica".
export async function aplicarCalculoInformes(companyId: string, itemId: string, estado: EstadoItem): Promise<void> {
  const { data, error } = await supabase.from('checklist_item_estados').select('estado').eq('company_id', companyId).eq('item_id', itemId).maybeSingle();
  if (error) throw new Error(`No se pudo leer el estado de ${LEGAL_INFORMES[itemId] ?? itemId}.`);
  if ((data as { estado: string } | null)?.estado === 'no_aplica') throw new Error(`${LEGAL_INFORMES[itemId] ?? itemId} está marcado como "No aplica": no se cambia.`);
  const { error: e2 } = await supabase.from('checklist_item_estados').upsert(
    { company_id: companyId, item_id: itemId, estado, auto_source: FUENTE_INFORMES_PUESTO, updated_at: new Date().toISOString() },
    { onConflict: 'company_id,item_id' });
  if (e2) throw new Error(e2.code === '42501' ? 'No tienes permiso para cambiar el Checklist.' : `No se pudo aplicar el cálculo en ${LEGAL_INFORMES[itemId] ?? itemId}.`);
}
